import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import TagSelect from '../../Tags/TagSelect';
import PropTypes from 'prop-types';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import Loading from '../../Loading';
import EmptyState from '../../Common/EmptyState';

const columnHelper = createColumnHelper();
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TAG_COLORS = [
  'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200'
];

const colorForTag = label => {
  const hash = String(label || '')
    .split('')
    .reduce((total, char) => total + char.charCodeAt(0), 0);
  return TAG_COLORS[hash % TAG_COLORS.length];
};

const normalizeEmail = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const InlineIconButton = ({
  label,
  onClick,
  children,
  tone = 'slate',
  disabled = false
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-40 ${
      tone === 'green'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
        : tone === 'red'
          ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300'
          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-100 dark:border-strokedark dark:bg-boxdark-2 dark:text-slate-300'
    }`}
  >
    {children}
  </button>
);

InlineIconButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  tone: PropTypes.string,
  disabled: PropTypes.bool
};

const DeveloperTable = ({
  data,
  tagData,
  loading,
  saving,
  onEdit,
  onMerge,
  onUnmerge,
  isDarkMode,
  editingDeveloperId,
  formData,
  setFormData,
  onSave,
  onCancel,
  selectedRows,
  onSelectionChange
}) => {
  const [filterText, setFilterText] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [sorting, setSorting] = useState([]);
  const [addingEmail, setAddingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [tagPicker, setTagPicker] = useState(null);
  const [editFocus, setEditFocus] = useState(null);
  const [inlineError, setInlineError] = useState('');
  const tagPickerRef = useRef(null);
  const pageSize = 10;

  const filteredData = useMemo(() => {
    if (!filterText) return data;
    const query = filterText.toLowerCase();
    return data.filter(
      item =>
        String(item.author || '')
          .toLowerCase()
          .includes(query) ||
        (Array.isArray(item.author_email)
          ? item.author_email
          : [item.author_email]
        )
          .filter(Boolean)
          .join(',')
          .toLowerCase()
          .includes(query)
    );
  }, [data, filterText]);

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, pageIndex]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const currentRangeStart =
    filteredData.length === 0 ? 0 : pageIndex * pageSize + 1;
  const currentRangeEnd = Math.min(
    (pageIndex + 1) * pageSize,
    filteredData.length
  );

  const resetInlineControls = () => {
    setAddingEmail(false);
    setEmailDraft('');
    setTagPicker(null);
    setEditFocus(null);
    setInlineError('');
  };

  useEffect(() => {
    if (!tagPicker) return undefined;
    const closeTagPickerOutside = event => {
      if (
        tagPickerRef.current?.contains(event.target) ||
        event.target.closest?.('.developer-tag-select__menu')
      ) {
        return;
      }
      setTagPicker(false);
    };
    document.addEventListener('mousedown', closeTagPickerOutside);
    return () =>
      document.removeEventListener('mousedown', closeTagPickerOutside);
  }, [tagPicker]);

  const beginEdit = (developer, field) => {
    if (onEdit(developer)) {
      resetInlineControls();
      setEditFocus(field);
      if (field === 'email') setAddingEmail(true);
      if (field === 'tags') setTagPicker(true);
    }
  };

  const updateEmails = emails =>
    setFormData(previous => ({ ...previous, emails }));

  const commitEmail = () => {
    const nextEmail = normalizeEmail(emailDraft);
    if (!EMAIL_PATTERN.test(nextEmail)) {
      setInlineError('Enter a valid email address.');
      return;
    }
    const emails = formData.emails || [];
    if (emails.some(email => normalizeEmail(email) === nextEmail)) {
      setInlineError('This email is already assigned to the developer.');
      return;
    }
    updateEmails([...emails, nextEmail]);
    setAddingEmail(false);
    setEmailDraft('');
    setInlineError('');
  };

  const removeEmail = index => {
    const emails = formData.emails || [];
    if (emails.length <= 1) {
      setInlineError('At least one developer email is required.');
      return;
    }
    updateEmails(emails.filter((_email, emailIndex) => emailIndex !== index));
    setInlineError('');
  };

  const tagOptions = Object.entries(tagData).map(([value, label]) => ({
    value,
    label
  }));

  const applyTagSelection = selectedOptions => {
    const seen = new Set();
    const tags = (selectedOptions || []).reduce((result, option) => {
      const label = String(option.label || '').trim();
      const key = label.toLowerCase();
      if (!label || seen.has(key)) return result;
      seen.add(key);
      const isNew =
        option.isNew ||
        option.__isNew__ ||
        String(option.value || '').startsWith('new:');
      result.push({
        value: isNew
          ? `new:${String(option.value || label).replace(/^new:/, '')}`
          : option.value,
        label,
        isNew: Boolean(isNew)
      });
      return result;
    }, []);
    setFormData(previous => ({ ...previous, tags }));
    setInlineError('');
  };

  const toggleAll = () => {
    const ids = paginatedData.map(item => item._id);
    const next = new Set(selectedRows);
    const allSelected = ids.every(id => next.has(id));
    ids.forEach(id => (allSelected ? next.delete(id) : next.add(id)));
    onSelectionChange(next);
  };

  const toggleRow = id => {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const renderEmailChips = (developer, isEditing) => {
    const emails = isEditing
      ? formData.emails || []
      : developer.author_email || [];
    return (
      <div className="min-w-[260px]">
        <div className="flex flex-wrap items-center gap-2">
          {emails.map((email, index) => (
            <span
              key={email}
              className="group inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
            >
              {email}
              {isEditing && (
                <span className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                  <button
                    type="button"
                    aria-label={`Remove ${email}`}
                    onClick={() => removeEmail(index)}
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                  </button>
                </span>
              )}
            </span>
          ))}
          {isEditing && addingEmail ? (
            <input
              autoFocus
              type="email"
              value={emailDraft}
              onChange={event => setEmailDraft(event.target.value)}
              onBlur={() => {
                if (emailDraft) commitEmail();
                else setAddingEmail(false);
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitEmail();
                }
              }}
              placeholder="name@company.com"
              className="w-56 rounded-xl border border-blue-400 bg-white px-3 py-1.5 text-sm outline-none ring-4 ring-blue-100 dark:bg-meta-4 dark:text-white dark:ring-blue-900/40"
            />
          ) : isEditing ? (
            <InlineIconButton
              label="Add email"
              onClick={() => {
                setEmailDraft('');
                setTagPicker(false);
                setAddingEmail(true);
              }}
            >
              <AddRoundedIcon fontSize="small" />
            </InlineIconButton>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTagChips = (developer, isEditing) => {
    const tags = isEditing
      ? formData.tags || []
      : (developer.tags || []).map(value => ({
          value,
          label: tagData[value] || value
        }));
    return (
      <div className="min-w-[220px]">
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag, index) => (
            <span
              key={`${tag.value}-${index}`}
              className={`group inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${colorForTag(
                tag.label
              )}`}
            >
              {tag.label}
              {isEditing && (
                <span className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                  <button
                    type="button"
                    aria-label={`Remove ${tag.label} tag`}
                    onClick={() =>
                      setFormData(previous => ({
                        ...previous,
                        tags: previous.tags.filter(
                          (_item, tagIndex) => tagIndex !== index
                        )
                      }))
                    }
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                  </button>
                </span>
              )}
            </span>
          ))}
          {isEditing && (
            <InlineIconButton
              label="Add tag"
              onClick={() => setTagPicker(current => !current)}
            >
              <AddRoundedIcon fontSize="small" />
            </InlineIconButton>
          )}
        </div>
        {isEditing && tagPicker && (
          <div ref={tagPickerRef} className="mt-2 w-64">
            <TagSelect
              allowCreate
              autoFocus
              menuIsOpen
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : undefined
              }
              menuPosition="fixed"
              isMulti
              value={formData.tags || []}
              options={tagOptions}
              onChange={applyTagSelection}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              isClearable={false}
              components={{ ClearIndicator: null, IndicatorSeparator: null }}
              placeholder="Search or create tag..."
              classNamePrefix="developer-tag-select"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        )}
      </div>
    );
  };

  const hasChanges = developer => {
    const originalEmails = (developer.author_email || []).map(normalizeEmail);
    const originalTags = (developer.tags || []).map(String);
    const currentTags = (formData.tags || [])
      .filter(tag => !tag.isNew)
      .map(tag => String(tag.value));
    return (
      String(formData.name || '').trim() !==
        String(developer.author || '').trim() ||
      JSON.stringify(formData.emails || []) !==
        JSON.stringify(originalEmails) ||
      JSON.stringify(currentTags) !== JSON.stringify(originalTags) ||
      (formData.tags || []).some(tag => tag.isNew)
    );
  };

  const columns = [
    {
      id: 'select',
      header: () => {
        const ids = paginatedData.map(item => item._id);
        const allSelected =
          ids.length > 0 && ids.every(id => selectedRows.has(id));
        return (
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            className="h-4 w-4 rounded"
          />
        );
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedRows.has(row.original._id)}
          disabled={editingDeveloperId === row.original._id}
          onChange={() => toggleRow(row.original._id)}
          className="h-4 w-4 rounded"
        />
      ),
      size: 40
    },
    columnHelper.accessor('author', {
      header: ({ column }) => (
        <button
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500"
        >
          Name{' '}
          {column.getIsSorted() === 'asc'
            ? '↑'
            : column.getIsSorted() === 'desc'
              ? '↓'
              : ''}
        </button>
      ),
      cell: ({ row }) => {
        const isEditing = editingDeveloperId === row.original._id;
        return isEditing ? (
          <input
            autoFocus={editFocus === 'name'}
            value={formData.name || ''}
            onChange={event =>
              setFormData(previous => ({
                ...previous,
                name: event.target.value
              }))
            }
            className="min-w-[180px] rounded-xl border border-blue-400 bg-white px-3 py-2 font-semibold outline-none ring-4 ring-blue-100 dark:bg-meta-4 dark:text-white dark:ring-blue-900/40"
          />
        ) : (
          <button
            type="button"
            onClick={() => beginEdit(row.original, 'name')}
            className="min-w-[180px] text-left"
          >
            <span className="block font-semibold text-slate-900 dark:text-white">
              {row.original.author}
            </span>
            <span className="mt-1 block text-xs text-slate-400">
              Click to edit
            </span>
          </button>
        );
      },
      enableSorting: true
    }),
    columnHelper.accessor('author_email', {
      header: () => (
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          Emails
        </span>
      ),
      cell: ({ row }) => {
        const isEditing = editingDeveloperId === row.original._id;
        return (
          <div
            onClick={event => {
              event.stopPropagation();
              if (!isEditing) beginEdit(row.original, 'email');
            }}
            role={!isEditing ? 'button' : undefined}
            tabIndex={!isEditing ? 0 : undefined}
          >
            {renderEmailChips(row.original, isEditing)}
          </div>
        );
      }
    }),
    columnHelper.accessor('tags', {
      header: () => (
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          Tags
        </span>
      ),
      cell: ({ row }) => {
        const isEditing = editingDeveloperId === row.original._id;
        return (
          <div
            onClick={event => {
              event.stopPropagation();
              if (!isEditing) beginEdit(row.original, 'tags');
            }}
            role={!isEditing ? 'button' : undefined}
            tabIndex={!isEditing ? 0 : undefined}
          >
            {renderTagChips(row.original, isEditing)}
          </div>
        );
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          Actions
        </span>
      ),
      cell: ({ row }) => {
        const isEditing = editingDeveloperId === row.original._id;
        const canUnmerge =
          Array.isArray(row.original.merge_history) &&
          row.original.merge_history.length > 0;
        if (isEditing) {
          const canSave =
            hasChanges(row.original) &&
            !inlineError &&
            (formData.emails || []).length > 0 &&
            String(formData.name || '').trim();
          return (
            <div className="flex gap-2">
              <InlineIconButton
                label="Save changes"
                tone="green"
                disabled={!canSave || saving}
                onClick={() => onSave(formData)}
              >
                <SaveRoundedIcon fontSize="small" />
              </InlineIconButton>
              <InlineIconButton
                label="Ignore changes"
                tone="red"
                disabled={saving}
                onClick={() => {
                  resetInlineControls();
                  onCancel();
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </InlineIconButton>
            </div>
          );
        }
        return canUnmerge ? (
          <InlineIconButton
            label="Unmerge latest developer merge"
            onClick={() => onUnmerge(row.original)}
          >
            <UndoRoundedIcon fontSize="small" />
          </InlineIconButton>
        ) : null;
      }
    })
  ];

  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  return (
    <div
      className="space-y-5"
      onKeyDown={event => {
        if (event.key === 'Escape' && editingDeveloperId) {
          resetInlineControls();
          onCancel();
        }
        if (
          event.key === 'Enter' &&
          !event.defaultPrevented &&
          editingDeveloperId &&
          !saving
        ) {
          const developer = data.find(item => item._id === editingDeveloperId);
          if (developer && hasChanges(developer) && !inlineError) {
            onSave(formData);
          }
        }
      }}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full max-w-xl">
          <SearchRoundedIcon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by developer name or email..."
            className={`w-full rounded-2xl border py-3 pl-11 pr-4 text-sm outline-none transition focus:ring-4 ${
              isDarkMode
                ? 'border-strokedark bg-meta-4 text-white focus:ring-blue-900/40'
                : 'border-slate-200 bg-slate-50 focus:ring-blue-100'
            }`}
            value={filterText}
            onChange={event => {
              setPageIndex(0);
              setFilterText(event.target.value);
            }}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <span className="rounded-full bg-slate-100 px-3 py-1.5 font-medium dark:bg-slate-800 dark:text-slate-200">
            {filteredData.length} matching records
          </span>
          {selectedRows.size > 0 && (
            <button
              type="button"
              onClick={onMerge}
              className="rounded-2xl bg-emerald-500 px-4 py-2.5 font-semibold text-white"
            >
              Merge Selected ({selectedRows.size})
            </button>
          )}
        </div>
      </div>

      {inlineError && (
        <p
          role="alert"
          className="rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-950/30 dark:text-red-300"
        >
          {inlineError}
        </p>
      )}

      <div
        className={`overflow-hidden rounded-[24px] border ${
          isDarkMode ? 'border-strokedark' : 'border-slate-200'
        }`}
      >
        <div className="max-w-full overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              {table.getHeaderGroups().map(group => (
                <tr
                  key={group.id}
                  className={
                    isDarkMode ? 'bg-meta-4 text-left' : 'bg-slate-50 text-left'
                  }
                >
                  {group.headers.map(header => (
                    <th
                      key={header.id}
                      className="min-w-[40px] px-4 py-4 font-medium"
                      style={{ width: header.column.columnDef.size }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody
              className={`divide-y ${
                isDarkMode ? 'divide-strokedark' : 'divide-slate-200'
              }`}
            >
              {loading ? (
                <tr>
                  <td colSpan={columns.length}>
                    <Loading type="table" />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8">
                    <EmptyState
                      compact
                      title="No Data Available"
                      description={
                        filterText
                          ? 'No developers matched your search.'
                          : 'There are no developer records to display yet.'
                      }
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => {
                  const isEditing = editingDeveloperId === row.original._id;
                  return (
                    <tr
                      key={row.id}
                      onClick={event => {
                        if (
                          isEditing ||
                          event.target.closest(
                            'button, input, [role="combobox"]'
                          )
                        ) {
                          return;
                        }
                        beginEdit(row.original, 'name');
                      }}
                      className={`${
                        isEditing
                          ? 'bg-blue-50/50 dark:bg-blue-950/10'
                          : 'bg-white hover:bg-slate-50/70 dark:bg-boxdark dark:hover:bg-white/5'
                      } transition`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-4 py-5 align-top">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 text-sm dark:border-strokedark">
          <span>
            Showing {currentRangeStart}-{currentRangeEnd} of{' '}
            {filteredData.length} developers
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={pageIndex === 0}
              onClick={() => setPageIndex(value => value - 1)}
              className="rounded-xl border px-3 py-2 disabled:opacity-40"
            >
              <KeyboardArrowLeftRoundedIcon fontSize="small" /> Previous
            </button>
            <span>
              Page {pageIndex + 1} of {totalPages}
            </span>
            <button
              disabled={pageIndex >= totalPages - 1}
              onClick={() => setPageIndex(value => value + 1)}
              className="rounded-xl border px-3 py-2 disabled:opacity-40"
            >
              Next <KeyboardArrowRightRoundedIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

DeveloperTable.propTypes = {
  data: PropTypes.array.isRequired,
  tagData: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onMerge: PropTypes.func.isRequired,
  onUnmerge: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  editingDeveloperId: PropTypes.string,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectedRows: PropTypes.instanceOf(Set).isRequired,
  onSelectionChange: PropTypes.func.isRequired
};

export default DeveloperTable;
